/**
 * Editor for the brief-template sections list. Owns the DnD context + table
 * chrome; row-level presentation lives in `BriefSectionRow`, editing lives in
 * the section drawer. Parent owns the source of truth and rebases
 * dirty/snapshot state.
 */
import { useMemo, useState } from 'react';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@monksflow/monks-ui';
import { LayoutTemplateIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSortableList } from '@/shared/hooks';
import { EmptyState } from '@/shared/ui/EmptyState';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import type { SectionDraft } from '../../lib/sectionDraft';
import { BriefSectionRow } from '../BriefSectionRow';

export interface BriefSectionsBlockProps {
  sections: SectionDraft[];
  disabled: boolean;
  onAdd: () => void;
  onOpenSection: (tempId: string) => void;
  onDelete: (tempId: string) => void;
  onReorder: (nextAll: SectionDraft[]) => void;
  /** Replaces the delete modal's warning, for a template whose sections reach other workspaces. */
  deleteWarning?: string;
}

export function BriefSectionsBlock(props: BriefSectionsBlockProps) {
  const { t } = useTranslation('knowledge');
  const { t: tCommon } = useTranslation('common');

  // Tombstoned rows stay in the underlying array for stable React keys, but
  // they must NOT participate in the sortable list or be rendered.
  const liveSections = useMemo(
    () => props.sections.filter((section) => !section.isDeleted),
    [props.sections],
  );
  const sortableIds = useMemo(() => liveSections.map((section) => section.tempId), [liveSections]);

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const pendingDeleteName =
    liveSections.find((section) => section.tempId === pendingDelete)?.name.trim() || undefined;

  const { sensors, handleDragEnd } = useSortableList(sortableIds, ({ oldIndex, newIndex }) => {
    const nextLive = arrayMove(liveSections, oldIndex, newIndex);
    // Splice live rows back into their slots, keeping tombstones in place so
    // the parent's stable-key invariant survives the reorder.
    const liveCursor = nextLive[Symbol.iterator]();
    const nextAll = props.sections.map((section) =>
      section.isDeleted ? section : (liveCursor.next().value ?? section),
    );
    props.onReorder(nextAll);
  });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    props.onDelete(pendingDelete);
    setPendingDelete(null);
  };

  const isEmpty = liveSections.length === 0;

  return (
    <Card
      compact
      data-testid="brief-template-sections-block"
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('knowledgeHub.briefSectionsBlock.briefSections')}</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={props.onAdd}
          disabled={props.disabled}
          data-testid="brief-template-section-add"
        >
          <PlusIcon aria-hidden className="size-md" />
          {t('knowledgeHub.briefSectionsBlock.add')}
        </Button>
      </CardHeader>
      <CardContent className="@container flex min-h-0 flex-1 flex-col overflow-y-auto">
        {isEmpty ? (
          <EmptyState
            data-testid="brief-template-sections-empty-state"
            icon={<LayoutTemplateIcon aria-hidden />}
            title={t('knowledgeHub.briefSectionsBlock.noBriefSectionsAddedYet')}
            description={t('knowledgeHub.briefSectionsBlock.clickAddToStartBuilding')}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            {/* Fixed layout so a long description truncates instead of pushing the actions column
                past the card edge: Tessra cells never wrap. Below 48rem (the preview panel open on
                a laptop) the fixed columns alone would fill the card, so Required and AI allowed,
                which the section drawer also shows, give way to the description. */}
            <Table
              aria-label={t('knowledgeHub.briefSectionsBlock.briefTemplateSections')}
              className="table-fixed"
            >
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="w-12"
                    aria-label={t('knowledgeHub.briefSectionsBlock.reorder')}
                  />
                  <TableHead className="w-40 text-muted-foreground @3xl:w-60">
                    {t('knowledgeHub.briefSectionsBlock.sectionName')}
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    {t('knowledgeHub.briefSectionsBlock.description')}
                  </TableHead>
                  <TableHead className="hidden w-29 text-muted-foreground @3xl:table-cell">
                    {t('knowledgeHub.briefSectionsBlock.requiredField')}
                  </TableHead>
                  <TableHead className="hidden w-29 text-muted-foreground @3xl:table-cell">
                    {t('knowledgeHub.briefSectionsBlock.aiAllowed')}
                  </TableHead>
                  <TableHead
                    className="w-22"
                    aria-label={t('knowledgeHub.briefSectionsBlock.actions')}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  {liveSections.map((section) => (
                    <BriefSectionRow
                      key={section.tempId}
                      tempId={section.tempId}
                      name={section.name}
                      description={section.instructions}
                      type={section.type}
                      required={section.required}
                      aiAllowed={section.aiAllowed}
                      draft={section.isNew}
                      disabled={props.disabled}
                      onOpen={props.onOpenSection}
                      onDelete={(tempId) => setPendingDelete(tempId)}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        )}
      </CardContent>
      <DeleteConfirmationModal
        open={pendingDelete !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirmDelete={confirmDelete}
        resourceType={tCommon('components.deleteConfirmationModal.section')}
        resourceName={pendingDeleteName}
        warningMessage={
          props.deleteWarning ?? t('knowledgeHub.briefSectionsBlock.thisSectionWillBeRemoved')
        }
        data-testid="brief-template-section-delete-modal"
      />
    </Card>
  );
}
